import { useTranslation } from 'next-i18next';
import { Dialog } from 'radix-ui';
import { type FC, useEffect, useState } from 'react';

import { Button } from '../landingV4/common/Button';
import { Checkbox } from '../landingV4/common/Checkbox';
import { Flex } from '../landingV4/common/Flex';
import { Text } from '../landingV4/common/Text';

import type { CookieConsentPreferences } from './cookieConsentStorage';
import styles from './CookieSettingsDialog.module.scss';

export type CookieSettingsDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialPreferences: CookieConsentPreferences;
  onSave: (prefs: CookieConsentPreferences) => void;
};

export const CookieSettingsDialog: FC<CookieSettingsDialogProps> = (props) => {
  const { open, onOpenChange, initialPreferences, onSave } = props;
  const { t } = useTranslation('common');

  const [analytics, setAnalytics] = useState(initialPreferences.analytics);
  const [marketing, setMarketing] = useState(initialPreferences.marketing);

  useEffect(() => {
    if (open) {
      setAnalytics(initialPreferences.analytics);
      setMarketing(initialPreferences.marketing);
    }
  }, [open, initialPreferences.analytics, initialPreferences.marketing]);

  const handleSave = (): void => {
    onSave({ analytics, marketing });
    onOpenChange(false);
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />

        <Dialog.Content className={styles.content}>
          <Flex direction="column" gap={10} className={styles.inner}>
            <Dialog.Title className={styles.dialogTitle}>
              <Text type="regular18">{t('cookieConsent.settingsTitle')}</Text>
            </Dialog.Title>

            <Dialog.Description className={styles.description}>
              {t('cookieConsent.settingsDescription')}
            </Dialog.Description>

            <Flex direction="column" gap={8}>
              <Checkbox
                inputId="cookie-necessary"
                checked
                disabled
                label={
                  <Flex direction="column" gap={4}>
                    <Text type="regular14">{t('cookieConsent.necessary')}</Text>

                    <Text type="regular12" className={styles.hint}>
                      {t('cookieConsent.necessaryHint')}
                    </Text>
                  </Flex>
                }
              />

              <Checkbox
                inputId="cookie-analytics"
                checked={analytics}
                onChange={(e) => setAnalytics(e.target.checked)}
                label={
                  <Flex direction="column" gap={4}>
                    <Text type="regular14">{t('cookieConsent.analytics')}</Text>

                    <Text type="regular12" className={styles.hint}>
                      {t('cookieConsent.analyticsHint')}
                    </Text>
                  </Flex>
                }
              />

              <Checkbox
                inputId="cookie-marketing"
                checked={marketing}
                onChange={(e) => setMarketing(e.target.checked)}
                label={
                  <Flex direction="column" gap={4}>
                    <Text type="regular14">{t('cookieConsent.marketing')}</Text>

                    <Text type="regular12" className={styles.hint}>
                      {t('cookieConsent.marketingHint')}
                    </Text>
                  </Flex>
                }
              />
            </Flex>

            <Flex gap={12} wrap="wrap" justifyContent="flexEnd">
              <Dialog.Close asChild>
                <Button type="button" variant="secondary">
                  {t('cookieConsent.cancel')}
                </Button>
              </Dialog.Close>

              <Button type="button" variant="primary" onClick={handleSave}>
                {t('cookieConsent.savePreferences')}
              </Button>
            </Flex>
          </Flex>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
